import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageShell } from '../components/layout/PageShell'
import { PageHeader } from '../components/layout/PageHeader'
import { FormField } from '../components/fields/FormField'
import { OwnershipCounter } from '../components/fields/OwnershipCounter'
import { inputClassName } from '../components/fields/fieldStyles'
import { Button } from '../components/ui/Button'
import { SectionTopAlert } from '../components/ui/SectionTopAlert'
import { StartOverButton } from '../components/ui/StartOverButton'
import { BackButton } from '../components/ui/BackButton'
import { SaveIndicator } from '../components/ui/SaveIndicator'
import { useFormStore } from '../store/formStore'
import { scrollToFirstError, FIRST_INVALID_FIELD } from '../lib/scrollToError'
import { REGIONS, REGION_KEYS } from '../constants/processingBreakdown'

export function ProcessingBreakdownScreen() {
  const navigate = useNavigate()
  const store = useFormStore()

  // MAF 26-05: surfaces the region-sum / volume errors only after a submit try.
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // The regional-split section is the scroll target for the sum gate: its
  // banner sits at the top of the box, so we anchor the box rather than the
  // banner itself and let scroll-margin-top clear the sticky chrome.
  const regionSectionRef = useRef<HTMLElement>(null)

  // Live region total drives the "Remaining" counter and the 100% hard gate.
  const regionSum = REGION_KEYS.reduce(
    (acc, k) => acc + (Number(store.formData[k]) || 0),
    0
  )
  const regionSumOk = regionSum === 100
  const estimatedMonthlyVolume = Number(store.formData.estimated_monthly_volume) || 0
  const monthlyVolumeOk = estimatedMonthlyVolume > 0

  // Persist a numeric Processing Breakdown field under its legacy id.
  function persistNumber(name: string, raw: string) {
    // Editing (or clearing) a field dismisses any visible validation errors;
    // they only return if the user tries to Continue again.
    setSubmitAttempted(false)
    store.setField(name, raw === '' ? 0 : Number(raw))
  }

  const ready = regionSumOk && monthlyVolumeOk

  function handleContinue() {
    if (!ready) {
      setSubmitAttempted(true)
      // The regional split sits above the volume field, so its 100% sum-gate
      // banner outranks any invalid field.
      scrollToFirstError(regionSumOk ? null : regionSectionRef.current, FIRST_INVALID_FIELD)
      return
    }
    store.setStep(4)
    navigate('/review')
  }

  return (
    <PageShell showStepper currentStep={3}>
      {/* Page heading + intro */}
      <PageHeader
        title="Processing Breakdown"
        intro="Tell us where your processing volume comes from so we can route it through the right Zota gateway technology."
      />

      {/* ───────────────────────────────────────────────────────────────────
          Box 1 — Major regions of traffic source (MAF 26-05)
      ─────────────────────────────────────────────────────────────────── */}
      <section
        ref={regionSectionRef}
        style={{
          background: 'var(--color-surface-container-lowest)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-8)',
          marginBottom: 'var(--spacing-8)',
        }}
      >
        {/* Box title + helper on the left, live remaining counter on the right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-8)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-title-md)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--color-text-primary)',
                margin: 0,
                lineHeight: 'var(--leading-tight)',
              }}
            >
              Regional traffic split
            </h2>
            {/* 100% rule hint */}
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--color-text-secondary)',
                lineHeight: 'var(--leading-normal)',
                margin: 0,
              }}
            >
              Percentages must add up to 100%.
            </p>
          </div>
          <OwnershipCounter
            allocated={regionSum}
            mode="remaining"
            hasError={submitAttempted && !regionSumOk}
          />
        </div>

        {/* Sum gate error — canonical boxed alert at the top of the section */}
        {submitAttempted && !regionSumOk && (
          <SectionTopAlert>
            Regional percentages must add up to 100%
          </SectionTopAlert>
        )}

        {/* 13 region percentage inputs, responsive grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'var(--spacing-4)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {REGIONS.map((r) => {
            // A region is "active" once it carries a value above 0: its label
            // turns brand blue. The field keeps its default border.
            const filled = Number(store.formData[r.key] ?? 0) > 0
            return (
            <FormField
              key={r.key}
              label={r.label}
              name={r.key}
              labelColor={filled ? 'var(--color-action-primary)' : undefined}
            >
              <input
                id={r.key}
                type="number"
                min={0}
                max={100}
                step={1}
                defaultValue={(store.formData[r.key] as number) ?? 0}
                onChange={(e) => persistNumber(r.key, e.target.value)}
                className={inputClassName}
              />
            </FormField>
            )
          })}
        </div>

      </section>

      {/* ───────────────────────────────────────────────────────────────────
          Box 2 — Estimated monthly volume (MAF 26-05)
      ─────────────────────────────────────────────────────────────────── */}
      <section
        style={{
          background: 'var(--color-surface-container-lowest)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-8)',
          marginBottom: 'var(--spacing-8)',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-md)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
            margin: '0 0 var(--spacing-8) 0',
            lineHeight: 'var(--leading-tight)',
          }}
        >
          Monthly volume
        </h2>
        <FormField
          label="Total across all target regions (USD)"
          name="estimated_monthly_volume"
          required
          error={
            submitAttempted && !monthlyVolumeOk
              ? 'Estimated monthly volume is required.'
              : undefined
          }
        >
          <input
            id="estimated_monthly_volume"
            type="number"
            min={0}
            step="any"
            aria-invalid={submitAttempted && !monthlyVolumeOk ? 'true' : undefined}
            placeholder="Estimated monthly volume (total USD)"
            defaultValue={
              (store.formData.estimated_monthly_volume as number | undefined) ?? ''
            }
            onChange={(e) => persistNumber('estimated_monthly_volume', e.target.value)}
            className={inputClassName}
          />
        </FormField>
      </section>

      {/* ─── CTA ────────────────────────────────────────────────────────── */}
      {/* Pulled up into the card's bottom margin so the required-fields note
          hugs the card above, with clear separation before the buttons. */}
      <div style={{ marginTop: 'calc(-1 * var(--spacing-4))' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 'var(--spacing-4)',
            minHeight: '20px',
            marginBottom: 'var(--spacing-10)',
          }}
        >
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-sm)',
              color: 'var(--color-text-tertiary)',
              margin: 0,
            }}
          >
            Fields marked with{' '}
            <span style={{ color: 'var(--color-action-primary)' }}>*</span> are required.
          </p>
          <SaveIndicator state={store.saveState} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-3)' }}>
          <BackButton to="/company-structure" />
          <Button
            variant="primary"
            onClick={handleContinue}
          >
            Continue to Review
          </Button>
          <StartOverButton inline />
        </div>
      </div>
    </PageShell>
  )
}
